import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  usePinnedFiltersStore,
  type SlotDraft,
} from '@/store/pinned-filters-store';
import type { FilterOperator } from '@/types/query';
import { NULLARY_OPS, NUMERIC_OPS, opLabelKey, parseValue } from '../column-types';

type Props = {
  column: string;
  sqlType: string;
};

/**
 * Pinned numeric slot: operator picker + one or two value inputs (two for
 * `between`). Writes through to the pinned-filters store draft on every
 * keystroke; the global Apply button performs the commit.
 */
export function NumericSlot({ column, sqlType }: Props): JSX.Element {
  const { t } = useTranslation();
  const draft = usePinnedFiltersStore((s) => s.drafts[column]);
  const setDraft = usePinnedFiltersStore((s) => s.setDraft);

  const { operator, primary, secondary } = useMemo(
    () => readDraft(draft),
    [draft],
  );
  const isBetween = operator === 'between';
  const isNullary = NULLARY_OPS.has(operator);

  function emit(op: FilterOperator, v1: string, v2: string): void {
    if (NULLARY_OPS.has(op)) {
      setDraft(column, { operator: op, value: null });
      return;
    }
    if (op === 'between') {
      setDraft(column, {
        operator: 'between',
        value: [
          v1 ? parseValue(v1, sqlType) : '',
          v2 ? parseValue(v2, sqlType) : '',
        ],
      });
      return;
    }
    setDraft(column, {
      operator: op,
      value: v1 ? parseValue(v1, sqlType) : '',
    });
  }

  function clear(): void {
    setDraft(column, null);
  }

  return (
    <div className="flex gap-1 items-stretch">
      <div className="flex flex-col gap-0.5 shrink-0">
        <label
          className="text-[10px]"
          style={{ color: 'var(--color-muted)' }}
        >
          {t('filters.pinned.operator')}
        </label>
        <select
          value={operator}
          onChange={(e) =>
            emit(e.target.value as FilterOperator, primary, secondary)
          }
          aria-label={t('filters.pinned.operatorAria', { column })}
          className="w-full text-xs p-1 rounded"
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-fg)',
          }}
        >
          {NUMERIC_OPS.map((op) => (
            <option key={op.value} value={op.value}>
              {t(opLabelKey(op.value))}
            </option>
          ))}
        </select>
      </div>
      {!isNullary && (
        <div className="flex flex-col flex-1 min-w-0 gap-0.5">
          <label
            className="text-[10px]"
            style={{ color: 'var(--color-muted)' }}
          >
            {isBetween
              ? t('filters.pinned.minValue')
              : t('filters.pinned.value')}
          </label>
          <input
            type="number"
            value={primary}
            onChange={(e) => emit(operator, e.target.value, secondary)}
            placeholder={
              isBetween
                ? t('filters.pinned.minPlaceholder')
                : t('filters.pinned.valuePlaceholder')
            }
            aria-label={t('filters.pinned.valueAria', { column })}
            className="w-full text-xs p-1 rounded"
            style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-fg)',
            }}
          />
        </div>
      )}
      {isBetween && (
        <div className="flex flex-col flex-1 min-w-0 gap-0.5">
          <label
            className="text-[10px]"
            style={{ color: 'var(--color-muted)' }}
          >
            {t('filters.pinned.maxValue')}
          </label>
          <input
            type="number"
            value={secondary}
            onChange={(e) => emit(operator, primary, e.target.value)}
            placeholder={t('filters.pinned.maxPlaceholder')}
            aria-label={t('filters.pinned.upperValueAria', { column })}
            className="w-full text-xs p-1 rounded"
            style={{
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              color: 'var(--color-fg)',
            }}
          />
        </div>
      )}
      <button
        type="button"
        onClick={clear}
        disabled={!draft}
        aria-label={t('filters.pinned.numericResetAria', { column })}
        className="text-xs px-1.5 rounded disabled:opacity-40 self-end mb-0.5"
        style={{
          border: '1px solid var(--color-border)',
          color: 'var(--color-muted)',
        }}
      >
        ×
      </button>
    </div>
  );
}

function readDraft(draft: SlotDraft | null | undefined): {
  operator: FilterOperator;
  primary: string;
  secondary: string;
} {
  if (!draft) return { operator: 'eq', primary: '', secondary: '' };
  if (draft.operator === 'between' && Array.isArray(draft.value)) {
    const [a, b] = draft.value as Array<string | number>;
    return {
      operator: 'between',
      primary: a == null ? '' : String(a),
      secondary: b == null ? '' : String(b),
    };
  }
  return {
    operator: draft.operator,
    primary:
      draft.value == null || Array.isArray(draft.value)
        ? ''
        : String(draft.value),
    secondary: '',
  };
}
